import { canonicalMemberProfileUpdateCommand, type UpdateMemberRequest } from "@billiard/contracts";

export interface MemberProfileUpdateSubmission {
  memberId: string;
  request: UpdateMemberRequest;
}

interface RequestFailureShape {
  code?: unknown;
  statusCode?: unknown;
  memberProfileUpdateGuardState?: unknown;
}

const memberProfileUpdateKnownPreCloudFailures = new Set([
  "MEMBER_PROFILE_UPDATE_GUARD_UNAVAILABLE",
  "ONLINE_SERVICE_REQUIRED",
  "CLOUD_COORDINATOR_UNAVAILABLE",
  "AUTHORIZATION_FACTS_NOT_TRUSTED",
  "AUTHORIZATION_CONTEXT_UNAVAILABLE",
  "WORKFORCE_MUTATION_OUTCOME_UNKNOWN",
  "AUTHORIZATION_CONTEXT_MISSING",
]);

export function memberProfileUpdateOutcomeIsUnknown(error: unknown): boolean {
  if (typeof error !== "object" || error === null) return false;
  const failure = error as RequestFailureShape;
  const statusCode = typeof failure.statusCode === "number" ? failure.statusCode : undefined;
  if (
    typeof failure.code === "string" &&
    memberProfileUpdateKnownPreCloudFailures.has(failure.code)
  ) {
    return false;
  }
  if (statusCode !== undefined && statusCode >= 400 && statusCode < 500 && statusCode !== 408) {
    return false;
  }
  if (
    failure.code === "MEMBER_PROFILE_UPDATE_RESULT_UNKNOWN" ||
    failure.code === "GATEWAY_UNREACHABLE" ||
    failure.code === "GATEWAY_RESPONSE_INVALID" ||
    failure.code === "CLOUD_UNREACHABLE" ||
    failure.code === "CLOUD_RESPONSE_INVALID"
  ) {
    return true;
  }
  return statusCode === 408 || (statusCode !== undefined && statusCode >= 500);
}

export function retainMemberProfileUpdateSubmission(
  error: unknown,
  submitted: MemberProfileUpdateSubmission,
  wasRetainedUnknown: boolean,
): MemberProfileUpdateSubmission | null {
  if (memberProfileUpdateOutcomeIsUnknown(error)) return submitted;
  if (
    wasRetainedUnknown &&
    (typeof error !== "object" ||
      error === null ||
      Reflect.get(error, "memberProfileUpdateGuardState") !== "CLEARED")
  ) {
    return submitted;
  }
  return null;
}

export function selectMemberProfileUpdateSubmission(
  retained: MemberProfileUpdateSubmission | null,
  next: MemberProfileUpdateSubmission,
): MemberProfileUpdateSubmission {
  return retained ?? next;
}

export function resolveMemberProfileUpdateSubmission(
  retained: MemberProfileUpdateSubmission | null,
  buildNext: () => MemberProfileUpdateSubmission,
): MemberProfileUpdateSubmission {
  return retained ?? buildNext();
}

export function memberProfileUpdateSubmitEnabled(input: {
  online: boolean;
  mutationPending: boolean;
  retainedUnknown: boolean;
  currentEditReady: boolean;
  hasEditTarget: boolean;
  recoveryChecked: boolean;
}): boolean {
  return (
    input.online &&
    !input.mutationPending &&
    input.recoveryChecked &&
    input.hasEditTarget &&
    (input.retainedUnknown || input.currentEditReady)
  );
}

export async function memberProfileUpdateCommandSha256(
  request: UpdateMemberRequest,
): Promise<string> {
  if (!globalThis.crypto?.subtle) {
    throw new Error("当前浏览器无法校验会员资料保存摘要，请升级浏览器后重试");
  }
  const digest = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(canonicalMemberProfileUpdateCommand(request)),
  );
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
